/**
 * WarmupPlanStep — paso 5 (último) del setup wizard.
 *
 * Informativo: explica el plan de warm-up de 14 días y pide que el usuario
 * acknowledge antes de activar la cuenta. Al confirmar:
 *  1. Marca warmupAcknowledged = true
 *  2. Activa la cuenta → pasa de unconfigured a warming_up (día 1, límite 50/d)
 */

import { useState } from "react";
import {
    Loader2,
    Shield,
    TrendingUp,
    AlertTriangle,
    Rocket,
} from "lucide-react";
import { Button } from "../../../../shared/components/ui/Button";
import { whatsappAccountsApi } from "../../api";
import type { WhatsAppAccount } from "../../types";

interface Props {
    account: WhatsAppAccount;
    onComplete: (updated: WhatsAppAccount) => void;
}

const WARMUP_PHASES = [
    { days: "1-3", limit: 50, label: "Arranque suave" },
    { days: "4-7", limit: 200, label: "Subida gradual" },
    { days: "8-14", limit: 500, label: "Subida fuerte" },
    { days: "15+", limit: -1, label: "Sin límite (según salud)" },
];

export default function WarmupPlanStep({ account, onComplete }: Props) {
    const [acknowledged, setAcknowledged] = useState(false);
    const [activating, setActivating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFinalize = async () => {
        if (!acknowledged) {
            setError("Marcá la casilla para confirmar que entendés el plan");
            return;
        }
        setActivating(true);
        setError(null);
        // 1. Ack del warm-up
        const ackRes = await whatsappAccountsApi.acknowledgeWarmup(account.id);
        if (!ackRes.success) {
            setActivating(false);
            setError(ackRes.error || "Error al confirmar ack");
            return;
        }
        // 2. Activar la cuenta → pasa a warming_up
        const actRes = await whatsappAccountsApi.activate(account.id);
        setActivating(false);
        if (!actRes.success) {
            setError(actRes.error || "Error al activar");
            return;
        }
        onComplete(actRes.data);
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-violet-50 text-violet-600">
                    <Shield size={16} />
                </div>
                <div>
                    <h3 className="text-base font-bold text-slate-900">
                        Plan de calentamiento del número
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        WhatsApp bloquea los números nuevos que mandan miles de
                        mensajes de golpe. Para proteger el tuyo, subimos el límite
                        de envíos de a poco durante 14 días.
                    </p>
                </div>
            </div>

            {/* Tabla de fases */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {WARMUP_PHASES.map((phase, i) => (
                    <div
                        key={i}
                        className={`flex items-center gap-3 p-3 ${
                            i < WARMUP_PHASES.length - 1
                                ? "border-b border-slate-100"
                                : ""
                        } ${i === 0 ? "bg-green-50/40" : ""}`}
                    >
                        <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                i === 0
                                    ? "bg-green-100 text-green-700 ring-2 ring-green-300"
                                    : "bg-slate-100 text-slate-600"
                            }`}
                        >
                            D{phase.days.split("-")[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-slate-900">
                                    Día {phase.days}
                                </span>
                                {i === 0 && (
                                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-green-200 text-green-800">
                                        Empezás acá
                                    </span>
                                )}
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                                {phase.label}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-bold text-slate-900 tabular-nums">
                                {phase.limit === -1
                                    ? "∞"
                                    : phase.limit.toLocaleString("es-AR")}
                            </p>
                            <p className="text-[10px] text-slate-500">msj/día</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Auto-cancel info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5">
                <AlertTriangle
                    size={14}
                    className="text-amber-600 mt-0.5 flex-shrink-0"
                />
                <div className="text-[11px] text-amber-800 leading-relaxed">
                    <p className="font-semibold mb-1">Auto-pausa por seguridad</p>
                    <p>
                        Si la salud del número cae (tasa de bloqueo &gt; 3% o reportes
                        de spam &gt; 5), el sistema pausa automáticamente esta cuenta
                        hasta que la revises desde el panel de salud.
                    </p>
                </div>
            </div>

            {/* Ack checkbox */}
            <label className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded text-green-600 focus:ring-green-500 focus:ring-2"
                />
                <span className="text-xs text-slate-700 leading-relaxed">
                    Entiendo el plan de calentamiento. Acepto que durante los primeros
                    14 días las campañas que envíe desde esta cuenta van a tener un
                    límite de envíos por día según la tabla de arriba.
                </span>
            </label>

            {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">
                    {error}
                </div>
            )}

            <div className="flex justify-end">
                <Button
                    variant="primary"
                    onClick={handleFinalize}
                    disabled={activating || !acknowledged}
                >
                    {activating ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Rocket size={14} />
                    )}
                    Activar cuenta
                </Button>
            </div>

            <p className="text-[10px] text-slate-400 text-center flex items-center justify-center gap-1">
                <TrendingUp size={10} />
                Al activar, la cuenta queda lista para enviar campañas (con el límite
                de envíos del día 1).
            </p>
        </div>
    );
}
